import { hashOf } from "../hash.js";

/**
 * The 18 Rocketbox office looks (Microsoft Rocketbox, MIT): 11 `Business_*` in suits and
 * business wear, 7 `Adults` in smart office wear. The id is the folder name under
 * `public/world/models/rocketbox/`; the letter after the dash is the motion look (f or m).
 */
export const BUSINESS_IDS = [
  "business-f1",
  "business-f2",
  "business-f3",
  "business-f4",
  "business-m1",
  "business-m2",
  "business-m3",
  "business-m4",
  "business-m5",
  "business-m6",
  "business-m7",
] as const;
export const OFFICE_IDS = [
  "office-f1",
  "office-f2",
  "office-f3",
  "office-f4",
  "office-m1",
  "office-m2",
  "office-m3",
] as const;
export const ROCKETBOX_IDS: readonly string[] = [...BUSINESS_IDS, ...OFFICE_IDS];

export type MotionLook = "f" | "m";

export function motionLookOf(avatarId: string): MotionLook {
  return /-f\d+$/.test(avatarId) ? "f" : "m";
}

/**
 * The look of one agent: a hash of the id picks it, the lead prefers the business wear. With
 * `taken` (the looks already used on the floor, in marker order) a clash moves on to the next
 * free look, so a floor of up to 18 people never shows the same person twice. Deterministic for
 * a given id and floor roster.
 */
export function rocketboxLook(
  agentId: string,
  role: "lead" | "worker",
  taken: ReadonlySet<string> = new Set(),
): string {
  const pool = role === "lead" ? BUSINESS_IDS : ROCKETBOX_IDS;
  const h = hashOf(`rocketbox:${agentId}`);
  const start = h % pool.length;
  for (let i = 0; i < pool.length; i++) {
    const id = pool[(start + i) % pool.length]!;
    if (!taken.has(id)) return id;
  }
  return pool[start]!;
}

/** Looks for a whole floor, in order (the lead first). */
export function assignLooks(
  agents: readonly { id: string; role: "lead" | "worker" }[],
): Map<string, string> {
  const taken = new Set<string>();
  const out = new Map<string, string>();
  // The lead picks first so a worker never takes the business look the lead would get.
  const ordered = [...agents].sort((a, b) => (a.role === b.role ? 0 : a.role === "lead" ? -1 : 1));
  for (const a of ordered) {
    const id = rocketboxLook(a.id, a.role, taken);
    taken.add(id);
    out.set(a.id, id);
  }
  return out;
}

export type SeatClip =
  "seatIdle" | "seatWait" | "seatBreathe" | "seatThink" | "seatLook" | "seatStretch";
export const SEAT_CLIPS: readonly SeatClip[] = [
  "seatIdle",
  "seatWait",
  "seatBreathe",
  "seatThink",
  "seatLook",
  "seatStretch",
];

export interface ClipPlan {
  /** The looping clip the person sits in. */
  base: SeatClip;
  /** One-shot gestures played now and then, in a loop of the list. */
  gestures: SeatClip[];
  /** Seconds between gestures (from the end of the last one). */
  every: number;
  /** 0..1: where in the base loop the person starts (so a row of people does not move as one). */
  phase: number;
}

/** The deterministic motion of one agent (the female look has the extra stretch clip). */
export function clipPlan(agentId: string, role: "lead" | "worker", look: MotionLook): ClipPlan {
  const h = hashOf(`motion:${agentId}`);
  const bases: SeatClip[] = ["seatIdle", "seatBreathe", "seatWait"];
  const gest: SeatClip[] = ["seatThink", "seatLook"];
  if (look === "f") gest.push("seatStretch");
  const order = [...gest];
  const rot = (h >>> 5) % order.length;
  return {
    base: role === "lead" ? "seatWait" : bases[h % bases.length]!,
    gestures: order.slice(rot).concat(order.slice(0, rot)),
    every: 9 + ((h >>> 9) % 14),
    phase: ((h >>> 3) % 1000) / 1000,
  };
}
